import { TRACE_MAX_EVENT_BYTES, TraceEvent } from './trace-event.interface'

//* Lo que reemplaza a un valor oculto. En español y entre comillas angulares para que quien lee la
//* bitácora entienda que ahí había algo y que se quitó a propósito.
export const REDACTED = '‹oculto›'

/**
 * Claves cuyo VALOR no se guarda nunca. La comparación es por inclusión sobre la clave normalizada,
 * así que `refreshToken`, `refresh_token` y `Refresh-Token` caen las tres.
 *
 * Los cuatro últimos son contenedores completos: si un error del servidor llegara a devolver el eco
 * de la petición, `headers` traería el `Authorization` y `body` la contraseña del login. Se tiran
 * enteros en vez de confiar en que ningún día vuelvan.
 */
const FORBIDDEN_KEYS = [
  'authorization',
  'token',
  'jwt',
  'bearer',
  'cookie',
  'password',
  'contrasena',
  'pwd',
  'secret',
  'apikey',
  'ciec',
  'clabe',
  'cardnumber',
  'cvv',
  'otp',
  'pin',
  'headers',
  'requestdetails',
  'rawbody',
  'credentials',
]

export interface RedactLimits {
  maxDepth: number
  maxKeys: number
  maxArray: number
  maxString: number
}

export const DEFAULT_LIMITS: RedactLimits = { maxDepth: 3, maxKeys: 12, maxArray: 5, maxString: 200 }

/**
 * Límites para el CUERPO de una petición, más generosos que los de un detalle cualquiera.
 *
 * Con los de por defecto, los filtros de este panel —que anidan
 * `{ filters: { campo: { value, matchMode } } }`, cuatro niveles— salían cortados en «‹…›» justo en
 * el dato que se quería ver. El cuerpo es el sitio donde suele estar el error, así que se le da
 * sitio; el presupuesto por paso sigue siendo el tope real, y si no cabe se descarta solo el cuerpo.
 */
export const BODY_LIMITS: RedactLimits = { maxDepth: 6, maxKeys: 30, maxArray: 10, maxString: 300 }

const JWT_RE = /\b(?:Bearer\s+)?eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g
const DATA_URL_RE = /data:[a-z]+\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+/gi
//* Desde 15 dígitos y no desde 13: un epoch en milisegundos tiene 13 y la bitácora va llena de ellos.
const LONG_DIGITS_RE = /\b(\d{15,19})\b/g

function normalizeKey(key: string): string {
  return key
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function isForbidden(key: string): boolean {
  const k = normalizeKey(key)
  return FORBIDDEN_KEYS.some((f) => k.includes(f))
}

/**
 * Limpia un texto de secretos incrustados y lo acota.
 *
 * Se aplica a TODO string que sobreviva, no solo a los de claves sospechosas: el caso real es un
 * mensaje de error que trae el token dentro de la frase.
 */
export function redactText(value: string, max = DEFAULT_LIMITS.maxString): string {
  let out = value.replace(JWT_RE, '‹jwt›').replace(DATA_URL_RE, '‹binario›')
  out = out.replace(LONG_DIGITS_RE, (_m, digits: string) => `****${digits.slice(-4)}`)
  return out.length > max ? `${out.slice(0, max)}…` : out
}

/**
 * Redacta cualquier valor antes de que entre al buffer.
 *
 * !! Corre al CAPTURAR el paso, no al enviarlo. Es la diferencia que importa: un token o una CIEC
 * nunca llegan a estar en memoria ni en sessionStorage, así que tampoco los vería alguien que
 * abriera las herramientas del navegador.
 *
 * Lo que NO oculta, por decisión explícita: montos, RFC, nombres y domicilios. Sin ellos la bitácora
 * deja de servir para reproducir el caso concreto. Quién puede leerlos se controla en el servidor,
 * que recorta los payloads para todo el que no sea del equipo de TI ni haya reportado.
 */
export function redactValue(value: unknown, limits: RedactLimits = DEFAULT_LIMITS, depth = 0, seen = new WeakSet<object>()): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return redactText(value, limits.maxString)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (typeof value === 'function') return undefined
  if (value instanceof Date) return value.toISOString()

  //* Lo que no se puede serializar de forma útil se etiqueta por lo que es. Un File dentro de la
  //* bitácora sería un objeto vacío y haría creer que no se mandó nada.
  if (typeof File !== 'undefined' && value instanceof File) return '‹archivo›'
  if (typeof Blob !== 'undefined' && value instanceof Blob) return '‹archivo›'
  if (typeof FormData !== 'undefined' && value instanceof FormData) return '‹formulario›'
  if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) return '‹elemento›'

  if (depth >= limits.maxDepth) return '‹…›'
  if (seen.has(value as object)) return '‹circular›'
  seen.add(value as object)

  if (Array.isArray(value)) {
    const out: unknown[] = value.slice(0, limits.maxArray).map((v) => redactValue(v, limits, depth + 1, seen))
    if (value.length > limits.maxArray) out.push(`…+${value.length - limits.maxArray}`)
    return out
  }

  const entries = Object.entries(value as Record<string, unknown>)
  const out: Record<string, unknown> = {}
  for (const [k, v] of entries.slice(0, limits.maxKeys)) {
    out[k] = isForbidden(k) ? REDACTED : redactValue(v, limits, depth + 1, seen)
  }
  if (entries.length > limits.maxKeys) out['…'] = `+${entries.length - limits.maxKeys}`
  return out
}

/**
 * Acota un paso al presupuesto. Si se pasa, se tira el detalle entero y se conserva la etiqueta:
 * un paso sin payload sigue contando la historia; un paso que no está, no.
 */
export function fitEvent(event: TraceEvent, maxBytes = TRACE_MAX_EVENT_BYTES): TraceEvent {
  if (byteLength(event) <= maxBytes) return event

  //! Se degrada POR PARTES, no de golpe. Antes se tiraba el `data` entero, y con un cuerpo grande
  //! eso se llevaba por delante el método, la ruta y el estado: el paso quedaba mudo justo en la
  //! petición más interesante, que es la que lleva más datos. Ahora lo primero que cae es el
  //! cuerpo —lo voluminoso—, y solo si aun así no cabe se descarta el resto.
  const body = (event.data as Record<string, unknown> | undefined)?.['body']
  if (body !== undefined) {
    const { body: _, ...restoDelDetalle } = event.data as Record<string, unknown>
    const recortado: TraceEvent = { ...event, data: { ...restoDelDetalle, body: '‹cuerpo demasiado grande›' } }
    if (byteLength(recortado) <= maxBytes) return recortado
  }

  const { data, ...rest } = event
  return rest
}

/**
 * Tamaño serializado aproximado, para llevar la suma corrida del buffer.
 *
 * Se estima sobre la longitud del texto en vez de construir un `Blob`: esto corre en CADA paso que
 * se captura, incluidos los clics, y crear un objeto solo para medirlo es trabajo que se nota. La
 * diferencia real es lo que ocupan los acentos en UTF-8, y para un presupuesto de 48 kb no cambia
 * ninguna decisión.
 */
export function byteLength(value: unknown): number {
  try {
    const text = JSON.stringify(value) ?? ''
    let bytes = text.length
    //* Un recargo por los caracteres fuera de ASCII, que en UTF-8 ocupan más de un byte.
    for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) > 127) bytes++
    return bytes
  } catch {
    return 0
  }
}
